import { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useQuery } from '@tanstack/react-query';

import {
  approveCollectionPlan,
  fetchCollectionPlan,
  fetchCollectionPlanVersions,
} from '#/api/crmMgmt/customerOrder/collection';
import { PERM } from '#/config/permissions';
import { Button } from '#/modules/commonComponents/button';
import { useConfirmDialog } from '#/modules/commonComponents/dialog';
import { SelectCore } from '#/modules/commonComponents/form/select';
import {
  COLLECTION_PLAN_SOURCE_LABEL,
  COLLECTION_PLAN_STATUS_LABEL,
  canApprovePlan,
  canEditManualPlan,
  labelOf,
} from '#/modules/crmMgmt/customerOrder/helper/collection';
import { useCan } from '#/modules/infra/auth/useCan';
import { formatDateOrDash } from '#/utils/date';
import { formatDisplayAmount } from '#/utils/formatNumber';

import CollectionPlanEditDialog from './CollectionPlanEditDialog';
import CollectionSimpleTable from './CollectionSimpleTable';
import CollectionStatusBadge from './CollectionStatusBadge';
import { collectionQueryKey } from './collectionQueryKeys';
import useCollectionMutation from './useCollectionMutation';

const PHASE_COLUMNS = [
  { id: 'phaseNo', header: '期別', accessorKey: 'phaseNo', size: 70, enableSorting: false },
  {
    id: 'amount',
    header: '金額',
    accessorFn: (row) => formatDisplayAmount(row.amount),
    size: 120,
    enableSorting: false,
    meta: { cellClassName: 'td_right' },
  },
  {
    id: 'percentage',
    header: '%',
    accessorFn: (row) => (row.percentage == null ? '—' : `${formatDisplayAmount(row.percentage)}%`),
    size: 80,
    enableSorting: false,
    meta: { cellClassName: 'td_right' },
  },
  {
    id: 'paymentTerm',
    header: '付款條件',
    accessorFn: (row) => [row.paymentTermCodeSnapshot, row.paymentTermNameSnapshot]
      .filter(Boolean).join(' - ') || '—',
    size: 180,
    enableSorting: false,
  },
  {
    id: 'dueDate',
    header: '到期日',
    accessorFn: (row) => formatDateOrDash(row.dueDate),
    size: 120,
    enableSorting: false,
  },
];

const CollectionPlanCard = ({ detail }) => {
  const customerOrderId = detail.id;
  const updatedAt = detail.record?.updatedAt ?? detail.updatedAt;
  const canUpdate = useCan(PERM.CUSTOMER_ORDER_UPDATE);
  const canReview = useCan(PERM.CUSTOMER_ORDER_REVIEW);
  const { confirm, ConfirmDialogHost } = useConfirmDialog();
  const [editOpen, setEditOpen] = useState(false);
  const [selectedPlanId, setSelectedPlanId] = useState('');

  const planQuery = useQuery({
    queryKey: collectionQueryKey(customerOrderId, 'plan'),
    queryFn: () => fetchCollectionPlan(customerOrderId),
  });
  const versionsQuery = useQuery({
    queryKey: collectionQueryKey(customerOrderId, 'planVersions'),
    queryFn: () => fetchCollectionPlanVersions(customerOrderId),
  });

  const currentPlan = planQuery.data?.data;
  const versions = useMemo(() => versionsQuery.data?.data ?? [], [versionsQuery.data]);
  const shownPlan = versions.find((plan) => plan.id === selectedPlanId) ?? currentPlan;
  // 最新一版（可能是待核准的人工草稿），編輯以它為底
  const latestPlan = versions.reduce(
    (latest, plan) => (!latest || plan.versionNo > latest.versionNo ? plan : latest),
    null,
  ) ?? currentPlan;

  const approveMutation = useCollectionMutation(
    customerOrderId,
    (plan) => approveCollectionPlan(customerOrderId, plan.id, {
      expectedVersionNo: plan.versionNo,
      expectedCustomerOrderUpdatedAt: updatedAt,
    }),
    { successMessage: '已核准收款計畫' },
  );

  async function handleApprove(plan) {
    const ok = await confirm({
      title: '核准收款計畫',
      message: `核准第 ${plan.versionNo} 版後即成為目前有效的收款約定，確定核准？`,
      confirmText: '核准',
    });
    if (ok) {
      approveMutation.mutate(plan);
    }
  }

  if (planQuery.isLoading) {
    return <p className="px-4 py-5 text-sm text-slate-500">載入收款計畫中…</p>;
  }
  if (!currentPlan?.id) {
    return <p className="px-4 py-5 text-sm text-muted-foreground">尚無收款計畫（客訂提交後由系統建立）</p>;
  }

  const versionOptions = versions.map((plan) => ({
    value: plan.id,
    label: `第 ${plan.versionNo} 版・${labelOf(COLLECTION_PLAN_STATUS_LABEL, plan.status)}`,
  }));
  const showEdit = canUpdate && canEditManualPlan(detail.status, currentPlan);
  const approvable = canReview && canApprovePlan(latestPlan) ? latestPlan : null;

  return (
    <div className="space-y-2">
      {ConfirmDialogHost}
      <div className="flex flex-wrap items-center gap-2 px-4 pt-3 text-sm">
        <CollectionStatusBadge status={shownPlan.status} labels={COLLECTION_PLAN_STATUS_LABEL} />
        <span className="text-slate-600">
          第 {shownPlan.versionNo} 版・{labelOf(COLLECTION_PLAN_SOURCE_LABEL, shownPlan.source)}
          ・{shownPlan.currencyCodeSnapshot ?? ''} {formatDisplayAmount(shownPlan.customerOrderTotalAmount)}
        </span>
        {currentPlan.revisionRequired ? (
          <span className="rounded bg-amber-100 px-2 py-0.5 text-amber-800">
            客訂金額或付款條件已異動，需編列並核准新版本
          </span>
        ) : null}
        <div className="ml-auto flex items-center gap-2">
          {versionOptions.length > 1 ? (
            <div className="w-48">
              <SelectCore
                id="collection-plan-version"
                options={versionOptions}
                value={shownPlan.id}
                onChange={(value) => setSelectedPlanId(value)}
              />
            </div>
          ) : null}
          {approvable ? (
            <Button
              type="button"
              size="sm"
              disabled={approveMutation.isPending}
              onClick={() => handleApprove(approvable)}
            >
              核准第 {approvable.versionNo} 版
            </Button>
          ) : null}
          {showEdit ? (
            <Button type="button" size="sm" variant="outline" onClick={() => setEditOpen(true)}>
              編輯分期
            </Button>
          ) : null}
        </div>
      </div>
      <CollectionSimpleTable
        rows={shownPlan.phases ?? []}
        columns={PHASE_COLUMNS}
        emptyText="此版本沒有期別"
        rowIdPrefix="phase"
      />
      {editOpen ? (
        <CollectionPlanEditDialog
          open={editOpen}
          onOpenChange={setEditOpen}
          customerOrderId={customerOrderId}
          customerOrderUpdatedAt={updatedAt}
          totalAmount={currentPlan.customerOrderTotalAmount}
          plan={latestPlan}
        />
      ) : null}
    </div>
  );
};

CollectionPlanCard.propTypes = {
  detail: PropTypes.shape({
    id: PropTypes.string.isRequired,
    status: PropTypes.string,
    updatedAt: PropTypes.string,
    record: PropTypes.shape({ updatedAt: PropTypes.string }),
  }).isRequired,
};

export default CollectionPlanCard;
